import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';

import { AppVersionService } from '@app/services/app-version.service';
import { DashboardsAbstract } from '@dashboards/components/abstract/dashboards.abstract';
import { selectSelectedDashboardMode } from '@dashboards/store/selectors/dashboards.selectors';
import {
  boardsSection,
  calendarSection,
  homeSection,
  inspectionsSection,
  maintenanceSection,
  managementSection,
  reportsSection,
  rolesSection,
  turnoversSection,
} from '@shared/constants/side-panel-config.const';
import { DashboardMode } from '@shared/enums/dashboard-mode';
import { MainPageActions } from '@shared/enums/main-page-actions';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { checkPermissionLevel } from '@shared/functions/check-permission-level.function';
import { isUserEmailDomainAllowed } from '@shared/functions/check-user-email-domain';
import { MenuSection, MenuSectionItem } from '@shared/interfaces/menu-config.interface';
import { UserData } from '@shared/interfaces/user-data';
import { selectUserData, selectUserPermissionLevel } from '@shared/selectors/user.selectors';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

@UntilDestroy()
@Component({
  selector: 'app-side-panel',
  templateUrl: './side-panel.component.html',
  styleUrls: ['./side-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidePanelComponent extends DashboardsAbstract implements OnInit, OnDestroy {
  menuConfig: MenuSection[] = [];
  currentVersionTimestamp = 0;
  readonly homeDashboardPermissions = [
    PermissionLevelType.Administrator_Dashboard_Read,
    PermissionLevelType.AssetManager_PropertyDashboard_Read,
    PermissionLevelType.ConstructionManager_Dashboard_Read,
    PermissionLevelType.PropertyManager_Dashboard_Read,
    PermissionLevelType.Turnovers_Kanban_Read, // TODO  - temporary permission for Vendor
  ];

  currentSectionBSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  @Input() isSidePanelExpanded = true;
  @Output() mainPageActionEvent: EventEmitter<MainPageActions> = new EventEmitter<MainPageActions>();

  constructor(
    protected cdr: ChangeDetectorRef,
    private store: Store<{}>,
    private appVersionService: AppVersionService,
    private snackbarService: SnackbarService
  ) {
    super(cdr);
  }

  ngOnInit(): void {
    this.appVersionService.version$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap(appVersion => {
          if (this.currentVersionTimestamp && this.currentVersionTimestamp !== appVersion.version_timestamp) {
            const url = new URL(window.location.href);
            url.searchParams.append('version', appVersion.version_number);
            this.snackbarService.warning(
              `🎉 New PropUp version. Plz <a class="text-color white underline" href="${url.toString()}">reload</a> your browser.`,
              24 * 60 * 60 * 1000,
              false
            );
          }
          this.currentVersionTimestamp = appVersion.version_timestamp;
        })
      )
      .subscribe();

    this.store
      .select(selectUserData)
      .pipe(
        untilDestroyed(this),
        filter((userData: UserData) => !!userData),
        tap((userData: UserData) => {
          this.userData = userData;
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    combineLatest([
      this.store.select(selectSelectedDashboardMode).pipe(filter((dashboardMode: DashboardMode) => !!dashboardMode)),
      this.store
        .select(selectUserPermissionLevel)
        .pipe(filter((permissionList: PermissionLevelType[]) => !!permissionList)),
      this.store.select(selectUserData).pipe(filter((userData: UserData) => !!userData?.email)),
    ])
      .pipe(
        untilDestroyed(this),
        tap(async ([dashboardMode, permissionList, userData]) => {
          this.dashboardMode = dashboardMode;
          this.permissionList = permissionList;
          this.userData = userData;
          this.menuConfig = this.getMenuConfig();
          this.cdr.detectChanges();
        })
      )
      .subscribe();
  }

  menuAction(action: MainPageActions) {
    this.mainPageActionEvent.emit(action);
  }

  private getMenuConfig(): MenuSection[] {
    const menuConfig: MenuSection[] = [];
    if (!this.dashboardMode) {
      return menuConfig;
    }

    if (this.permissionList.includes(PermissionLevelType.AllPermissions)) {
      this.checkRolesSection(menuConfig);
    } else {
      const availableDashboardPermissionsCount = this.homeDashboardPermissions.filter(permission =>
        this.permissionList.includes(permission)
      ).length;

      if (availableDashboardPermissionsCount === 1) {
        this.checkHomeSection(menuConfig);
      } else if (availableDashboardPermissionsCount > 1) {
        this.checkRolesSection(menuConfig);
      }
    }
    this.checksMainSection(menuConfig, turnoversSection);
    this.checksMainSection(menuConfig, boardsSection);
    this.checksMainSection(menuConfig, calendarSection);
    this.checksMainSection(menuConfig, inspectionsSection);
    this.checksMainSection(menuConfig, maintenanceSection);
    this.checksMainSection(menuConfig, reportsSection);
    // this.checksMainSection(menuConfig, ticketsSection);
    this.checksMainSection(menuConfig, managementSection);
    return menuConfig;
  }

  ngOnDestroy(): void {}

  private checkHomeSection(sections: MenuSection[]) {
    const section: MenuSection = { ...homeSection };
    sections.push(section);
  }

  private checkRolesSection(sections: MenuSection[]) {
    this.checksMainSection(sections, rolesSection);
  }

  private checksMainSection(sections: MenuSection[], newSection: MenuSection) {
    const section: MenuSection = { ...newSection };
    const sectionItem: MenuSectionItem[] = this.checksSubsection(section.sectionItem);

    const hasRights = this.isHasRightToSection(section, 'any');
    if (!hasRights) {
      return;
    }
    section.sectionItem = sectionItem;
    sections.push(section);
  }

  private checksSubsection(sectionItem: MenuSectionItem[]): MenuSectionItem[] {
    if (!sectionItem?.length) {
      return [];
    }
    return sectionItem
      .map((item: MenuSectionItem) => ({ ...item, sectionItem: this.checksSubsection(item.sectionItem) }))
      .filter((item: MenuSectionItem) => this.isHasRightToSection(item, 'all'));
  }

  private isHasRightToSection(item: MenuSectionItem, checkPermissions: 'all' | 'any') {
    return (
      checkPermissionLevel(this.permissionList, item.permissionLevel, checkPermissions) &&
      isUserEmailDomainAllowed(this.userData.email, item.emailDomains) &&
      (!item.hideWithoutSubsection || item.sectionItem.length)
    );
  }
}
